import React, { useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import './PositionsSlider.css';

/**
 * PositionsSlider Component
 * Creates a slider that interpolates between multiple camera positions along a route
 * @param {Object} props
 * @param {Array} props.positions - Array of objects with {name, cameraPosition, lookAtPosition}
 * @param {Object} props.camera - Three.js camera ref
 * @param {Object} props.controls - OrbitControls ref
 * @param {Function} props.onPositionChange - Callback when position changes
 * @param {string} props.direction - 'horizontal' or 'vertical' slider orientation
 * @param {boolean} props.isRtl - Right-to-left layout flag
 * @param {Object} props.colors - Colors object with primary color
 */
const PositionsSlider = ({ 
    positions = [], 
    camera, 
    controls, 
    onPositionChange, 
    currentBuildingName, 
    setCurrentBuildingName, 
    direction = 'horizontal',
    style = {},
    isRtl = false,
    colors = { primary: '#ffffff' },
  } ) => {

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [tooltipText, setToolTipText] = useState(currentBuildingName || '');

  useEffect(() => {
    setToolTipText(currentBuildingName || '');
  }, [currentBuildingName]);
  /**
   * Interpolates between two Vector3 positions
   */
  const lerpVector3 = (start, end, t) => {
    return new THREE.Vector3(
      THREE.MathUtils.lerp(start.x, end.x, t),
      THREE.MathUtils.lerp(start.y, end.y, t),
      THREE.MathUtils.lerp(start.z, end.z, t)
    );
  };

  /**
   * Gets the building name for the current slider position
   */
  const getBuildingNameAtPosition = useCallback((value) => {
    if (!positions || positions.length === 0) return '';
    
    const index = Math.round(value);
    const clampedIndex = Math.max(0, Math.min(positions.length - 1, index));
    
    return positions[clampedIndex]?.name || '';
  }, [positions]);

  /**
   * Gets the previous building name
   */
  const getPreviousBuildingName = useCallback(() => {
    if (!positions || positions.length === 0) return '';
    const prevIndex = Math.max(0, Math.floor(currentIndex) - 1);
    return positions[prevIndex]?.name || '';
  }, [positions, currentIndex]);

  /**
   * Gets the next building name
   */
  const getNextBuildingName = useCallback(() => {
    if (!positions || positions.length === 0) return '';
    const nextIndex = Math.min(positions.length - 1, Math.ceil(currentIndex) + 1);
    return positions[nextIndex]?.name || '';
  }, [positions, currentIndex]);

  /**
   * Calculates the interpolated position based on slider value
   */
  const getInterpolatedPosition = useCallback((value) => {
    if (!positions || positions.length === 0) return null;
    
    // Handle edge cases
    if (value <= 0) return positions[0];
    if (value >= positions.length - 1) return positions[positions.length - 1];

    // Find the two positions to interpolate between
    const lowerIndex = Math.floor(value);
    const upperIndex = Math.ceil(value);
    const t = value - lowerIndex;

    const lowerPos = positions[lowerIndex];
    const upperPos = positions[upperIndex];

    // Interpolate camera position
    const cameraPos = lerpVector3(
      new THREE.Vector3(lowerPos.cameraPosition.x, lowerPos.cameraPosition.y, lowerPos.cameraPosition.z),
      new THREE.Vector3(upperPos.cameraPosition.x, upperPos.cameraPosition.y, upperPos.cameraPosition.z),
      t
    );

    // Interpolate lookAt position
    const lookAtPos = lerpVector3(
      new THREE.Vector3(lowerPos.lookAtPosition.x, lowerPos.lookAtPosition.y, lowerPos.lookAtPosition.z),
      new THREE.Vector3(upperPos.lookAtPosition.x, upperPos.lookAtPosition.y, upperPos.lookAtPosition.z),
      t
    );

    return {
      cameraPosition: cameraPos,
      lookAtPosition: lookAtPos
    };
  }, [positions]);

  /**
   * Updates camera and controls based on slider position
   */
  const updateCameraPosition = useCallback((value) => {
    if (!camera?.current || !controls?.current) return;

    const interpolated = getInterpolatedPosition(value);
    if (!interpolated) return;

    const { cameraPosition, lookAtPosition } = interpolated;

    // Update camera position
    camera.current.position.set(
      cameraPosition.x,
      cameraPosition.y,
      cameraPosition.z
    );

    // Update controls target (lookAt position)
    controls.current.target.set(
      lookAtPosition.x,
      lookAtPosition.y,
      lookAtPosition.z
    );

    controls.current.update();

    // Call callback if provided
    if (onPositionChange) {
      onPositionChange(value, interpolated);
    }
  }, [camera, controls, getInterpolatedPosition, onPositionChange]);

  /**
   * Handle slider change
   */
  const handleSliderChange = (e) => {
    const value = parseFloat(e.target.value);
    // console.log("Slider changed to", value);
    setCurrentIndex(value);
    updateCameraPosition(value);
    
    const buildingName = getBuildingNameAtPosition(value);
    if (buildingName !== tooltipText) {
        setCurrentBuildingName(buildingName);
    }
    setToolTipText(buildingName);

  };

  /**
   * Animate through all positions
   */
  const playAnimation = useCallback(() => {
    if (isAnimating || positions.length < 2) return;

    setIsAnimating(true);
    let currentValue = 0;
    const duration = 5000; // 5 seconds total
    const fps = 60;
    const totalSteps = (duration / 1000) * fps;
    const increment = (positions.length - 1) / totalSteps;

    const interval = setInterval(() => {
      currentValue += increment;
      
      if (currentValue >= positions.length - 1) {
        currentValue = positions.length - 1;
        clearInterval(interval);
        setIsAnimating(false);
      }

      setCurrentIndex(currentValue);
      setCurrentBuildingName(getBuildingNameAtPosition(currentValue));
      updateCameraPosition(currentValue);
    }, 1000 / fps);
  }, [positions, isAnimating, updateCameraPosition, getBuildingNameAtPosition]);

  /**
   * Navigate to previous position
   */
  const goToPrevious = () => {
    const newIndex = Math.max(0, Math.floor(currentIndex) - 1);
    setCurrentIndex(newIndex);
    setCurrentBuildingName(getBuildingNameAtPosition(newIndex));
    updateCameraPosition(newIndex);
    const buildingName = getBuildingNameAtPosition(newIndex);
    if (buildingName !== tooltipText) {
        setToolTipText(buildingName);
    }
  };

  /**
   * Navigate to next position
   */
  const goToNext = () => {
    const newIndex = Math.min(positions.length - 1, Math.ceil(currentIndex) + 1);
    setCurrentIndex(newIndex);
    setCurrentBuildingName(getBuildingNameAtPosition(newIndex));
    updateCameraPosition(newIndex);
        const buildingName = getBuildingNameAtPosition(newIndex);
    if (buildingName !== tooltipText) {
        setToolTipText(buildingName);
    }
  };

  /**
   * Reset to start
   */
  const reset = () => {
    setCurrentIndex(0);
    setCurrentBuildingName(getBuildingNameAtPosition(0));
    updateCameraPosition(0);
  };


  if (!positions || positions.length === 0) {
    return null;
  }

  return (
    <>
      <style>{`
        .positions-slider-container.${direction} .position-slider::-webkit-slider-thumb:hover {
          background: ${colors?.primary || '#ffffff'} !important;
        }
        .positions-slider-container.${direction} .position-slider::-moz-range-thumb:hover {
          background: ${colors?.primary || '#ffffff'} !important;
        }
      `}</style>
      <div className={`positions-slider-container ${direction}`}
          style={{
              left: 0,
              top: 0,
              padding: '10px 7px',
              ...style 
          }}>
        <div className="positions-slider-controls">
        {/* <button 
          onClick={reset} 
          disabled={isAnimating || currentIndex === 0}
          className="slider-btn"
          title="Reset to start"
        >
          ⏮
        </button> */}
        
        <div className="button-with-tooltip">
          <button 
            onClick={direction === 'vertical' ? goToNext : goToPrevious} 
            disabled={isAnimating || (direction === 'vertical' ?  currentIndex >= positions.length - 1 : currentIndex === 0)}
            className="slider-btn"
            style={{ borderColor: colors.primary }}
            title="Previous position"
          >
            {direction === 'vertical' ? '▲' : '◀'}
          </button>
          {(direction === 'vertical' ? getNextBuildingName() : getPreviousBuildingName()) && (
            <div 
              className="button-tooltip"
              style={direction === 'vertical' && !isRtl ? { left: 'auto', right: '45px' } : {}}
            >
              {direction === 'vertical' ? getNextBuildingName() : getPreviousBuildingName()}
            </div>
          )}
        </div>

        <div className="slider-wrapper">
          <input
            type="range"
            min="0"
            max={positions.length - 1}
            step="0.01"
            value={currentIndex}
            onChange={handleSliderChange}
            disabled={isAnimating}
            className="position-slider"
            orient={direction === 'vertical' ? 'vertical' : 'horizontal'}
          />
          {tooltipText && (
            <div 
              className="slider-tooltip"
              style={{
                [direction === 'vertical' ? 'top' : 'left']: 
                  direction === 'vertical' 
                    ? `${100 - (currentIndex / (positions.length - 1)) * 100}%`
                    : `${(currentIndex / (positions.length - 1)) * 100}%`,
                ...(direction === 'vertical' && isRtl ? {right: 'auto', left: '30px'} : { left: 'auto', right: '30px' })
              }}
            >
              {tooltipText}
            </div>
          )}
          {/* <div className="slider-info">
            <span>Position: {(currentIndex + 1).toFixed(1)} / {positions.length}</span>
          </div> */}
        </div>

        {/* <button 
          onClick={playAnimation} 
          disabled={isAnimating}
          className="slider-btn slider-btn-play"
          title="Play animation"
        >
          {isAnimating ? '⏸' : '▶'}
        </button> */}

        <div className="button-with-tooltip">
          <button 
            onClick={direction === 'vertical' ? goToPrevious : goToNext} 
            disabled={isAnimating || (direction === 'vertical' ? currentIndex === 0 : currentIndex >= positions.length - 1)}
            className="slider-btn"
            style={{ borderColor: colors.primary }}
            title="Next position"
          >
            {direction === 'vertical' ? '▼' : '▶'}
          </button>
          {(direction === 'vertical' ? getPreviousBuildingName() : getNextBuildingName()) && (
            <div 
              className="button-tooltip"
              style={direction === 'vertical' && isRtl ? { left: 'auto', left: '45px' } : {}}
            >
              {direction === 'vertical' ? getPreviousBuildingName() : getNextBuildingName()}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default PositionsSlider;
